// Google OAuth & Session Management Service
import { auth, googleProvider, db } from './firebase';
import { signInWithPopup, signOut, onAuthStateChanged, type User } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import type { DadProfile } from '../types';

type AuthListener = (profile: DadProfile | null) => void;
const listeners: AuthListener[] = [];

let currentProfile: DadProfile | null = null;
const LOCAL_STORAGE_PROFILE_KEY = 'project_dad_current_profile';

export function subscribeAuth(listener: AuthListener): () => void {
  listeners.push(listener);
  listener(currentProfile);
  return () => {
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

function notifyListeners() {
  if (currentProfile) {
    localStorage.setItem(LOCAL_STORAGE_PROFILE_KEY, JSON.stringify(currentProfile));
  } else {
    localStorage.removeItem(LOCAL_STORAGE_PROFILE_KEY);
  }
  listeners.forEach(fn => fn(currentProfile));
}

export function getCurrentProfile(): DadProfile | null {
  return currentProfile;
}

export function isAuthenticated(): boolean {
  return currentProfile !== null;
}

export async function loginWithGoogle(): Promise<DadProfile> {
  try {
    const cred = await signInWithPopup(auth, googleProvider);
    return await handleUserSession(cred.user);
  } catch (error: any) {
    if (error.code === 'auth/popup-closed-by-user') {
      console.log('[Pops Culture] User closed Google sign-in popup.');
      throw error;
    }
    console.error('[Pops Culture] Google Sign-In Error:', error);
    throw error;
  }
}

async function handleUserSession(user: User): Promise<DadProfile> {
  // Check banned email registry
  if (user.email) {
    const bannedRef = doc(db, 'bannedEmails', user.email.toLowerCase().trim());
    try {
      const bannedSnap = await getDoc(bannedRef);
      if (bannedSnap.exists()) {
        await signOut(auth);
        currentProfile = null;
        notifyListeners();
        throw new Error('This email address has been banned from Pops Culture for violating community guidelines.');
      }
    } catch (err: any) {
      if (err.message?.includes('banned')) throw err;
      console.warn('[Pops Culture] Banned check notice:', err);
    }
  }

  const userDocRef = doc(db, 'users', user.uid);
  const userDocSnap = await getDoc(userDocRef);

  const oauthName = user.displayName || 'Neighbor Dad';
  const oauthEmail = user.email || '';
  const oauthPhoto = user.photoURL || undefined;
  const oauthPhone = user.phoneNumber || undefined;
  const oauthVerified = user.emailVerified;
  const oauthProvider = user.providerData?.[0]?.providerId || 'google.com';
  const oauthUid = user.providerData?.[0]?.uid || user.uid;

  if (userDocSnap.exists()) {
    const existing = userDocSnap.data() as DadProfile;

    if (existing.status === 'banned') {
      await signOut(auth);
      currentProfile = null;
      notifyListeners();
      throw new Error('This account has been banned from Pops Culture for violating community guidelines.');
    }

    const displayBase = existing.displayName?.trim() || oauthName;
    const initials = displayBase.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase() || 'ND';

    const updated: DadProfile = {
      ...existing,
      // Name and email are authoritative from Google OAuth and immutable by manual profile updates
      name: oauthName,
      email: oauthEmail,
      photoURL: oauthPhoto || existing.photoURL,
      phoneNumber: oauthPhone || existing.phoneNumber,
      emailVerified: oauthVerified,
      oauthProvider,
      oauthUid,
      avatarInitials: existing.avatarInitials || initials,
      lastLoginAt: Date.now(),
      status: existing.status || 'active'
    };
    try {
      await setDoc(userDocRef, {
        name: updated.name,
        email: updated.email,
        photoURL: updated.photoURL || null,
        phoneNumber: updated.phoneNumber || null,
        emailVerified: updated.emailVerified,
        oauthProvider,
        oauthUid,
        lastLoginAt: updated.lastLoginAt
      }, { merge: true });
    } catch (e) {
      console.warn('[Pops Culture] Failed to update OAuth profile in Firestore:', e);
    }
    currentProfile = updated;
  } else {
    // Brand new dad signing in via Google: initialize profile auto-filled with Google OAuth credentials
    const initials = oauthName.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase() || 'ND';

    const newProfile: DadProfile = {
      uid: user.uid,
      email: oauthEmail,
      name: oauthName,
      displayName: '', // Optional user-chosen display name
      avatarInitials: initials,
      photoURL: oauthPhoto,
      phoneNumber: oauthPhone,
      emailVerified: oauthVerified,
      oauthProvider,
      oauthUid,
      neighborhood: '',
      yearsAsDad: 1,
      beverage: '',
      bio: '',
      skills: [],
      kids: [],
      badgesEarned: [],
      crewCount: 0,
      crew: [],
      crewRequestsSent: [],
      crewRequestsReceived: [],
      status: 'active',
      createdAt: Date.now(),
      lastLoginAt: Date.now(),
      onboarded: false // Gated: must complete onboarding
    };

    try {
      await setDoc(userDocRef, newProfile);
    } catch (e) {
      console.warn('[Pops Culture] Failed to create new user in Firestore:', e);
    }
    currentProfile = newProfile;
  }

  notifyListeners();
  return currentProfile;
}

export async function updateDadProfile(updated: Partial<DadProfile>): Promise<DadProfile> {
  if (!currentProfile) throw new Error('No active user session');

  // Enforce immutability: Email and name cannot be updated via manual profile edits;
  // they must strictly retain the OAuth-provided values.
  const { name: _ignoredName, email: _ignoredEmail, ...allowedUpdates } = updated;

  let avatarInitials = currentProfile.avatarInitials;
  if (allowedUpdates.displayName !== undefined) {
    const effectiveName = allowedUpdates.displayName.trim() || currentProfile.name;
    const parts = effectiveName.split(' ');
    avatarInitials = parts.map(p => p[0]).join('').substring(0, 2).toUpperCase() || 'ND';
  }

  currentProfile = {
    ...currentProfile,
    ...allowedUpdates,
    avatarInitials
  };

  if (currentProfile.uid) {
    try {
      const ref = doc(db, 'users', currentProfile.uid);
      await setDoc(ref, currentProfile, { merge: true });
    } catch (e) {
      console.warn('[Pops Culture] Failed to sync profile update with Firestore:', e);
    }
  }

  notifyListeners();
  return currentProfile;
}

export function syncCurrentProfileWithDoc(freshProfile: DadProfile): void {
  if (currentProfile && currentProfile.uid === freshProfile.uid) {
    currentProfile = { ...currentProfile, ...freshProfile };
    notifyListeners();
  }
}

export async function logoutUser(): Promise<void> {
  try {
    await signOut(auth);
  } catch (e) {
    console.warn('[Pops Culture] Sign out error:', e);
  }
  currentProfile = null;
  notifyListeners();
}

// Live Auth State Observer for Firebase
onAuthStateChanged(auth, async (user) => {
  if (user) {
    await handleUserSession(user);
  } else {
    currentProfile = null;
    notifyListeners();
  }
});
